import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { TransactionState } from './types'
import { TransactionService } from '../services/TransactionService'
import { travelDetailInitialValues } from '../constants/transactionInitialValues'

export enum CustomerNationalityType {
  Thai = 'thai',
  Foreigner = 'foreigner'
}

const initialState: TransactionState = {
  // State
  isGetAllTransactionsLoading: false,
  isExportTransactionsLoading: false,
  isCreateTransactionLoading: false,
  isUpdateTransactionPerCustomerLoading: false,
  isCancelTransactionPerCustomerLoading: false,
  isCalculatePriceLoading: false,
  isDownloadCustomerTemplateLoading: false,
  isGetCustomerListFromExcelLoading: false,
  isExportTransactionCustomersLoading: false,
  isGetTravelDetailLoading: false,
  isGetInsurancePlanLoading: false,
  isGetCustomersLoading: false,
  isGetPriceLoading: false,
  isGetCustomerDetailLoading: false,
  isSearchCustomerExcelLoading: false,
  isGetInsurersLoading: false,
  isGetPlansLoading: false,
  isGetTransactionTimestampLoading: false,
  isGetTransactionStatusLoading: false,
  isUpdateTransactionStatusLoading: false,
  transactionInfo: travelDetailInitialValues,
  isActive: true,
  isOpenIntegrationLogDialog: { isOpen: false, currentTransactionId: '' },
  // Fetched
  fetchedInsurers: [],
  fetchedCustomerListFromExcel: []
}

const slice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    changeTransactionInfo: (state, action: PayloadAction<{ field: string; value: string | number }>) => {
      const value = action.payload.value
      const currentTransactionInfo = state.transactionInfo
      const currentField = action.payload.field!
      if (currentField === 'travelDate') state.transactionInfo.returnDate = ''
      state.transactionInfo = { ...currentTransactionInfo, [currentField]: value }
    },
    toggleTransactionActive: (state) => {
      state.isActive = !state.isActive
    },
    changeIntegrationLogDialog: (state, action: PayloadAction<{ isOpen: boolean; currentTransactionId: string }>) => {
      state.isOpenIntegrationLogDialog = action.payload
    },
    resetTransactionState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(TransactionService.getAllTransactionsAsyncThunk.pending, (state) => {
        state.isGetAllTransactionsLoading = true
      })
      .addCase(TransactionService.getAllTransactionsAsyncThunk.rejected, (state) => {
        state.isGetAllTransactionsLoading = false
      })
      .addCase(TransactionService.getAllTransactionsAsyncThunk.fulfilled, (state, action) => {
        state.isGetAllTransactionsLoading = false
        state.fetchedAllTransactions = action.payload
      })
      .addCase(TransactionService.exportTransactionsAsyncThunk.pending, (state) => {
        state.isExportTransactionsLoading = true
      })
      .addCase(TransactionService.exportTransactionsAsyncThunk.rejected, (state) => {
        state.isExportTransactionsLoading = false
      })
      .addCase(TransactionService.exportTransactionsAsyncThunk.fulfilled, (state) => {
        state.isExportTransactionsLoading = false
      })
      .addCase(TransactionService.createTransactionAsyncThunk.pending, (state) => {
        state.isCreateTransactionLoading = true
      })
      .addCase(TransactionService.createTransactionAsyncThunk.rejected, (state) => {
        state.isCreateTransactionLoading = false
      })
      .addCase(TransactionService.createTransactionAsyncThunk.fulfilled, (state) => {
        state.isCreateTransactionLoading = false
      })
      .addCase(TransactionService.updateTransactionPerCustomerAsyncThunk.pending, (state) => {
        state.isUpdateTransactionPerCustomerLoading = true
      })
      .addCase(TransactionService.updateTransactionPerCustomerAsyncThunk.rejected, (state) => {
        state.isUpdateTransactionPerCustomerLoading = false
      })
      .addCase(TransactionService.updateTransactionPerCustomerAsyncThunk.fulfilled, (state) => {
        state.isUpdateTransactionPerCustomerLoading = false
      })
      .addCase(TransactionService.cancelTransactionPerCustomerAsyncThunk.pending, (state) => {
        state.isCancelTransactionPerCustomerLoading = true
      })
      .addCase(TransactionService.cancelTransactionPerCustomerAsyncThunk.rejected, (state) => {
        state.isCancelTransactionPerCustomerLoading = false
      })
      .addCase(TransactionService.cancelTransactionPerCustomerAsyncThunk.fulfilled, (state) => {
        state.isCancelTransactionPerCustomerLoading = false
      })
      .addCase(TransactionService.calculatePriceAsyncThunk.pending, (state) => {
        state.isCalculatePriceLoading = true
      })
      .addCase(TransactionService.calculatePriceAsyncThunk.rejected, (state) => {
        state.isCalculatePriceLoading = false
      })
      .addCase(TransactionService.calculatePriceAsyncThunk.fulfilled, (state, action) => {
        state.isCalculatePriceLoading = false
        state.fetchedCalculatePrice = action.payload
      })
      .addCase(TransactionService.downloadCustomerTemplateAsyncThunk.pending, (state) => {
        state.isDownloadCustomerTemplateLoading = true
      })
      .addCase(TransactionService.downloadCustomerTemplateAsyncThunk.rejected, (state) => {
        state.isDownloadCustomerTemplateLoading = false
      })
      .addCase(TransactionService.downloadCustomerTemplateAsyncThunk.fulfilled, (state) => {
        state.isDownloadCustomerTemplateLoading = false
      })
      .addCase(TransactionService.getCustomerListFromExcelAsyncThunk.pending, (state) => {
        state.isGetCustomerListFromExcelLoading = true
      })
      .addCase(TransactionService.getCustomerListFromExcelAsyncThunk.rejected, (state) => {
        state.isGetCustomerListFromExcelLoading = false
      })
      .addCase(TransactionService.getCustomerListFromExcelAsyncThunk.fulfilled, (state, action) => {
        state.isGetCustomerListFromExcelLoading = false
        state.fetchedCustomerListFromExcel = action.payload.map((customer) => ({
          ...customer,
          identityNumber: customer.identityNumber === null ? '' : customer.identityNumber,
          passport: customer.passport === null ? '' : customer.passport
        }))
      })
      .addCase(TransactionService.exportTransactionCustomersAsyncThunk.pending, (state) => {
        state.isExportTransactionCustomersLoading = true
      })
      .addCase(TransactionService.exportTransactionCustomersAsyncThunk.rejected, (state) => {
        state.isExportTransactionCustomersLoading = false
      })
      .addCase(TransactionService.exportTransactionCustomersAsyncThunk.fulfilled, (state) => {
        state.isExportTransactionCustomersLoading = false
      })
      .addCase(TransactionService.getTravelDetailAsyncThunk.pending, (state) => {
        state.isGetTravelDetailLoading = true
      })
      .addCase(TransactionService.getTravelDetailAsyncThunk.rejected, (state) => {
        state.isGetTravelDetailLoading = false
      })
      .addCase(TransactionService.getTravelDetailAsyncThunk.fulfilled, (state, action) => {
        state.isGetTravelDetailLoading = false
        state.fetchedTravelDetail = action.payload
        state.timestamp = action.payload
        state.transactionInfo = {
          ...action.payload,
          totalPerson: action.payload.totalPerson.toString(),
          departureFlightId: action.payload.flight ? action.payload.flight.departureFlightId : '',
          departureFlightCode: action.payload.flight ? action.payload.flight.departureFlightCode : '',
          arrivalFlightId: action.payload.flight ? action.payload.flight.arrivalFlightId : '',
          arrivalFlightCode: action.payload.flight ? action.payload.flight.arrivalFlightCode : ''
        }
      })
      .addCase(TransactionService.getInsurancePlanAsyncThunk.pending, (state) => {
        state.isGetInsurancePlanLoading = true
      })
      .addCase(TransactionService.getInsurancePlanAsyncThunk.rejected, (state) => {
        state.isGetInsurancePlanLoading = false
      })
      .addCase(TransactionService.getInsurancePlanAsyncThunk.fulfilled, (state, action) => {
        state.isGetInsurancePlanLoading = false
        state.transactionInfo = {
          ...state.transactionInfo,
          page: '1',
          pageSize: '10',
          insurerId: action.payload.insurerId,
          insurancePlanId: action.payload.insurancePlanId
        }
        state.fetchedInsurancePlan = action.payload
        state.timestamp = action.payload
      })
      .addCase(TransactionService.getCustomersAsyncThunk.pending, (state) => {
        state.isGetCustomersLoading = true
      })
      .addCase(TransactionService.getCustomersAsyncThunk.rejected, (state) => {
        state.isGetCustomersLoading = false
      })
      .addCase(TransactionService.getCustomersAsyncThunk.fulfilled, (state, action) => {
        state.isGetCustomersLoading = false
        state.fetchedCustomers = action.payload
        state.timestamp = action.payload
      })
      .addCase(TransactionService.getPriceAsyncThunk.pending, (state) => {
        state.isGetPriceLoading = true
      })
      .addCase(TransactionService.getPriceAsyncThunk.rejected, (state) => {
        state.isGetPriceLoading = false
      })
      .addCase(TransactionService.getPriceAsyncThunk.fulfilled, (state, action) => {
        state.isGetPriceLoading = false
        state.fetchedPrice = action.payload
        state.timestamp = action.payload
      })
      .addCase(TransactionService.getCustomerDetailAsyncThunk.pending, (state) => {
        state.isGetCustomerDetailLoading = true
      })
      .addCase(TransactionService.getCustomerDetailAsyncThunk.rejected, (state) => {
        state.isGetCustomerDetailLoading = false
      })
      .addCase(TransactionService.getCustomerDetailAsyncThunk.fulfilled, (state, action) => {
        state.isGetCustomerDetailLoading = false
        state.fetchedCustomerDetail = action.payload
        state.timestamp = action.payload
      })
      .addCase(TransactionService.searchCustomerExcelAsyncThunk.pending, (state) => {
        state.isSearchCustomerExcelLoading = true
      })
      .addCase(TransactionService.searchCustomerExcelAsyncThunk.rejected, (state) => {
        state.isSearchCustomerExcelLoading = false
      })
      .addCase(TransactionService.searchCustomerExcelAsyncThunk.fulfilled, (state, action) => {
        state.isSearchCustomerExcelLoading = false
        state.getSearchCustomerListFromExcel = action.payload
      })
      .addCase(TransactionService.getInsurersAsyncThunk.pending, (state) => {
        state.isGetInsurersLoading = true
      })
      .addCase(TransactionService.getInsurersAsyncThunk.rejected, (state) => {
        state.isGetInsurersLoading = false
      })
      .addCase(TransactionService.getInsurersAsyncThunk.fulfilled, (state, action) => {
        state.isGetInsurersLoading = false
        state.fetchedInsurers = action.payload.map((i) => ({
          id: i.id,
          label: i.fullName
        }))
      })
      .addCase(TransactionService.getPlansAsyncThunk.pending, (state) => {
        state.isGetPlansLoading = true
      })
      .addCase(TransactionService.getPlansAsyncThunk.rejected, (state) => {
        state.isGetPlansLoading = false
      })
      .addCase(TransactionService.getPlansAsyncThunk.fulfilled, (state, action) => {
        state.isGetPlansLoading = false
        state.fetchedPlans = action.payload
      })
      .addCase(TransactionService.getTransactionTimestampAsyncThunk.pending, (state) => {
        state.isGetTransactionTimestampLoading = true
      })
      .addCase(TransactionService.getTransactionTimestampAsyncThunk.rejected, (state) => {
        state.isGetTransactionTimestampLoading = false
      })
      .addCase(TransactionService.getTransactionTimestampAsyncThunk.fulfilled, (state, action) => {
        state.isGetTransactionTimestampLoading = false
        state.fetchedTransactionTimestamp = action.payload
        state.timestamp = action.payload
      })
      .addCase(TransactionService.getTransactionStatusAsyncThunk.pending, (state) => {
        state.isGetTransactionStatusLoading = true
      })
      .addCase(TransactionService.getTransactionStatusAsyncThunk.rejected, (state) => {
        state.isGetTransactionStatusLoading = false
      })
      .addCase(TransactionService.getTransactionStatusAsyncThunk.fulfilled, (state, action) => {
        state.isGetTransactionStatusLoading = false
        state.fetchedTransactionStatus = action.payload
        state.isActive = action.payload
      })
      .addCase(TransactionService.updateTransactionStatusAsyncThunk.pending, (state) => {
        state.isUpdateTransactionStatusLoading = true
      })
      .addCase(TransactionService.updateTransactionStatusAsyncThunk.rejected, (state) => {
        state.isUpdateTransactionStatusLoading = false
      })
      .addCase(TransactionService.updateTransactionStatusAsyncThunk.fulfilled, (state) => {
        state.isUpdateTransactionStatusLoading = false
      })
  }
})

export const { resetTransactionState, changeTransactionInfo, toggleTransactionActive, changeIntegrationLogDialog } =
  slice.actions
export default slice.reducer
